import { navigate } from '@reach/router';
import { FormikHelpers } from 'formik';
import { useContext } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { PageContainer } from '../../../layout/Layout';
import { Alert } from '../../../shared/Alert';
import { Header1 } from '../../../shared/Headers';
import { CreateOrUpdateDocumentForm } from '../CreateOrUpdateDocumentForm';
import { DocumentFormModel, mapDocumentResponseToFormModel } from '../documentFormModel';
import { HandoverPackSectionContext } from '../documents-section/HandoverPackSectionContext';
import { documentsRoot } from '../documents-section/documentRoutes';
import { mapDocumentFormModelToEditCommand, UpdateDocumentDto } from './updateDocumentDto';

type Props = RouteComponentProps<{ documentId: string }>;

export const UpdateDocument = ({ documentId }: Props) => {
  const { allDocuments, refreshContext } = useContext(HandoverPackSectionContext);

  const parsedDocumentId = Number(documentId);

  const updateDocumentEndpoint = `documents/update/${documentId}`;

  const { error, sendRequest } = useApiRequest<undefined, UpdateDocumentDto>({
    endpointUrl: updateDocumentEndpoint,
    httpMethod: 'put',
  });

  if (documentId === undefined || isNaN(parsedDocumentId)) {
    return (
      <Alert
        type="error"
        title="Failed to read document ID"
        body="Failed to read document ID from URL, please double check URL before trying again"
      />
    );
  }

  const document = allDocuments.find(document => document.documentId === parsedDocumentId);

  if (document == undefined) {
    return (
      <Alert
        type="error"
        title="Document not found"
        body="Document with specified ID was not found, please double check URL before trying again"
      />
    );
  }

  const onSubmit = (formModel: DocumentFormModel, formikHelpers: FormikHelpers<DocumentFormModel>) => {
    void sendRequest({
      requestBody: mapDocumentFormModelToEditCommand(formModel, document.documentId),
      onSuccess: async () => {
        formikHelpers.setSubmitting(false);
        refreshContext();
        await navigate(documentsRoot);
      },
      onFailure: () => {
        formikHelpers.setSubmitting(false);
        formikHelpers.setStatus('Failed');
      },
    });
  };

  return (
    <PageContainer>
      <Header1>Update {document.documentName}</Header1>
      <CreateOrUpdateDocumentForm
        initialValues={mapDocumentResponseToFormModel(document)}
        onSubmit={onSubmit}
        error={error}
      />
    </PageContainer>
  );
};
